import React, {
	useState,
	useCallback,
	useEffect,
	useRef,
	useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import {
	getFacilityPhoneNumber,
	getFacilityCardPhoneNumber as getFacilityCardPhoneNumberAction,
} from 'store/modules/facilities';
import { getTollfreePhoneNumber } from 'store/modules/phone';
import { isSPMedium, isLarge } from 'utils/breakpoints';

import SearchPin from 'components/search/SearchMap/SearchPin';
import { SearchInfoWindow } from 'components/search/SearchMap';
import { NewFacilityCard } from 'components/facility/FacilityCard';
import FacilityCard from 'components/organisms/FacilityCard';

export function SearchMarker(props) {
	const {
		handleFacilitySelected,
		handleFacilityDeselected,
		handlePinSelected,
		handlePinDeselected,
		facility,
		google,
		map,
		isNewSearchVariant,
		onScrollToFacilityCard,
		pinSelected,
		showMiniCard,
		actions: actionsProp,
	} = props;

	const dispatch = useDispatch();
	const actions = useMemo(
		() =>
			actionsProp ?? {
				getFacilityPhoneNumber: /* istanbul ignore next */ (...args) =>
					dispatch(getFacilityPhoneNumber(...args)),
				getTollfreePhoneNumber: /* istanbul ignore next */ (...args) =>
					dispatch(getTollfreePhoneNumber(...args)),
				getFacilityCardPhoneNumber: (...args) =>
					dispatch(getFacilityCardPhoneNumberAction(...args)),
			},
		[actionsProp, dispatch],
	);

	const [overlayRef, setOverlayRef] = useState(null);
	const boundHandlerRef = useRef(null);

	const setOverlayNode = useCallback((node) => {
		setOverlayRef(node);
	}, []);

	const handleSelect = useCallback(() => {
		handleFacilitySelected(facility.id);
		handlePinSelected();

		// Pan map on mobile to position marker above bottom card/toolbar
		const hasMapObjects = google && map;
		const viewportWidth = window.innerWidth;
		const shouldAdjustForMobile =
			hasMapObjects &&
			(isNewSearchVariant
				? !isLarge(viewportWidth)
				: !isSPMedium(viewportWidth)); // [sf16978Variation]

		if (shouldAdjustForMobile) {
			const { latitude, longitude } = facility.location;
			const markerLatLng = new google.maps.LatLng(latitude, longitude);

			// First center on the marker
			map.panTo(markerLatLng);

			// New search variant needs a positive offset; legacy variant uses negative
			const panOffset = isNewSearchVariant ? 270 : -100; // [sf16978Variation]

			setTimeout(() => map.panBy(0, panOffset), 100);
		}
		// Desktop variant: scroll list to facility card instead of showing mini overlay
		if (isNewSearchVariant && isLarge(viewportWidth)) {
			// [sf16978Variation]
			onScrollToFacilityCard?.(facility.id);
		}
	}, [
		handleFacilitySelected,
		handlePinSelected,
		facility,
		google,
		map,
		isNewSearchVariant,
		onScrollToFacilityCard,
	]);

	const handleMouseEnter = useCallback(() => {
		if (pinSelected) return;
		handleFacilitySelected(facility.id);
	}, [handleFacilitySelected, facility, pinSelected]);

	const handleMouseLeave = useCallback(() => {
		if (pinSelected) return;
		handleFacilityDeselected();
	}, [handleFacilityDeselected, pinSelected]);

	const outsideClickHandler = useCallback(
		(event) => {
			if (!showMiniCard || !isNewSearchVariant) return; // only apply for new search variant [sf16978Variation]
			if (isLarge(window.innerWidth)) return; // desktop guard
			if (!overlayRef) return;

			// We are now listening on 'click' so internal button handlers run first.
			// Determine if the click target is inside the overlay.
			let inside = false;
			const target = event.target;

			if (overlayRef.contains(target)) {
				inside = true;
			} else if (typeof event.composedPath === 'function') {
				const path = event.composedPath();
				if (path && path.includes(overlayRef)) inside = true;
			}

			// Fallback: match known facility card wrapper classes from old variant for robustness
			if (!inside) {
				const closestCard = target.closest?.(
					'.search-overlay-card, .new-facility-card, .facility-card, .new-clp-facility-card',
				);
				if (closestCard && overlayRef.contains(closestCard))
					inside = true;
			}

			if (!inside) {
				// Outside click -> dismiss
				handleFacilityDeselected();
				handlePinDeselected();
			}
		},
		[
			showMiniCard,
			isNewSearchVariant,
			overlayRef,
			handleFacilityDeselected,
			handlePinDeselected,
		],
	);

	useEffect(() => {
		// Remove previous listener (defensive — cleanup normally handles this)
		/* istanbul ignore next */
		if (boundHandlerRef.current) {
			document.removeEventListener(
				'click',
				boundHandlerRef.current,
				true,
			);
			document.removeEventListener(
				'touchend',
				boundHandlerRef.current,
				true,
			);
			boundHandlerRef.current = null;
		}

		// Add new listener if conditions met
		if (
			showMiniCard &&
			isNewSearchVariant && // [sf16978Variation]
			!isLarge(window.innerWidth) &&
			overlayRef
		) {
			boundHandlerRef.current = outsideClickHandler;
			document.addEventListener('click', boundHandlerRef.current, true); // capture to run before map handlers
			document.addEventListener('touchend', boundHandlerRef.current, {
				passive: true,
				capture: true,
			});
		}

		return () => {
			if (boundHandlerRef.current) {
				document.removeEventListener(
					'click',
					boundHandlerRef.current,
					true,
				);
				document.removeEventListener(
					'touchend',
					boundHandlerRef.current,
					true,
				);
				boundHandlerRef.current = null;
			}
		};
	}, [showMiniCard, overlayRef, isNewSearchVariant, outsideClickHandler]);

	return (
		<>
			<SearchPin
				{...props}
				onClick={handleSelect}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
			/>
			{props.showMiniCard && (
				<>
					{props.isNewSearchVariant ? ( // [sf16978Variation]
						<div
							className="search-overlay-card"
							role="dialog"
							data-overlay-root="facility-overlay"
							ref={setOverlayNode}
						>
							<FacilityCard
								{...props}
								withUnits={false}
								responsive={false}
								withSchema={false}
								onVisible={props.handleFacilityCardVisible}
								miniCard
							/>
						</div>
					) : (
						<>
							<NewFacilityCard // here for the mobile version
								{...props}
								baseUrl={props.baseUrl}
								getFacilityCardPhoneNumber={() =>
									actions.getFacilityCardPhoneNumber(
										props.location.city,
										props.location.state,
										props.facility.id,
									)
								}
								onVisible={props.handleFacilityCardVisible}
								bottomMap
								miniCard
							/>
							<SearchInfoWindow {...props} />
						</>
					)}
				</>
			)}
		</>
	);
}

SearchMarker.propTypes = {
	selected: PropTypes.bool,
	handleFacilityCardVisible: PropTypes.func,
	handleFacilitySelected: PropTypes.func,
	handleFacilityDeselected: PropTypes.func,
	handlePinSelected: PropTypes.func,
	handlePinDeselected: PropTypes.func,
	showMiniCard: PropTypes.bool.isRequired,
	location: PropTypes.shape({
		city: PropTypes.string.isRequired,
		state: PropTypes.string.isRequired,
	}).isRequired,
	facility: PropTypes.shape({
		id: PropTypes.number.isRequired,
		location: PropTypes.shape({
			latitude: PropTypes.number,
			longitude: PropTypes.number,
		}),
	}).isRequired,
	baseUrl: PropTypes.string,
	actions: PropTypes.shape({
		getFacilityCardPhoneNumber: PropTypes.func.isRequired,
	}),
	google: PropTypes.shape({
		maps: PropTypes.shape({
			LatLng: PropTypes.func,
		}),
	}),
	map: PropTypes.shape({
		panTo: PropTypes.func,
		panBy: PropTypes.func,
	}),
	pinSelected: PropTypes.bool,
	isNewSearchVariant: PropTypes.bool,
	onScrollToFacilityCard: PropTypes.func,
};

export default SearchMarker;
